import { __decorate } from "tslib";
import { AutoEncoder, BooleanDecoder, EnumDecoder, field, IntegerDecoder, StringDecoder, URLDecoder } from '@common/encoding';
import { SimpleError } from '@common/errors';
import { Formatter } from '@stamhoofd/utility';
import { PaymentCustomer } from '../../PaymentCustomer.js';
import { PaymentMethod } from '../../PaymentMethod.js';
import { BalanceItemCartItem } from './BalanceItemCartItem.js';
import { IDRegisterCart, RegisterCart } from './RegisterCart.js';
import { upgradePriceFrom2To4DecimalPlaces } from '../../upgradePriceFrom2To4DecimalPlaces.js';
export class IDRegisterCheckout extends AutoEncoder {
    cart = IDRegisterCart.create({});
    administrationFee = 0;
    freeContribution = 0;
    /**
     * @deprecated removed, moved to cart.deleteRegistrations
     */
    _cancellationFeePercentage;
    paymentMethod = null;
    /**
     * The link we'll redirect the user back too after the payment page (either succeeded or failed!)
     * The id query param will be appended with the payment id
     */
    redirectUrl = null;
    /**
     * The link we'll redirect the user back too after the user canceled a payment (not supported for all payment methods)
     * The id query param will be appended with the payment id
     */
    cancelUrl = null;
    /**
     * Register these members as the organization
     */
    asOrganizationId = null;
    customer = null;
    /**
     * Cached price so we can detect inconsistencies between frontend and backend
     */
    totalPrice = null;
    sendConfirmationEmail = false;
    get organizationId() {
        return this.cart.organizationId;
    }
    hydrate(context) {
        const checkout = new RegisterCheckout();
        checkout.cart = this.cart.hydrate(context);
        checkout.administrationFee = this.administrationFee;
        checkout.freeContribution = this.freeContribution;
        checkout.paymentMethod = this.paymentMethod;
        checkout.asOrganizationId = this.asOrganizationId;
        checkout.customer = this.customer;
        checkout.sendConfirmationEmail = this.sendConfirmationEmail;
        if (context.organizations[0] && !checkout.cart.isEmpty && checkout.defaultOrganization === null) {
            const preferredId = checkout.singleOrganizationId;
            checkout.setDefaultOrganization((preferredId ? context.organizations.find(o => o.id === preferredId) : null) ?? context.organizations[0]);
        }
        else {
            if (!checkout.cart.isEmpty && !checkout.singleOrganization) {
                throw new Error('Missing default organization');
            }
        }
        return checkout;
    }
    get memberIds() {
        return Formatter.uniqueArray(this.cart.items.map(i => i.memberId));
    }
    get groupIds() {
        return Formatter.uniqueArray(this.cart.items.map(i => i.groupId));
    }
}
__decorate([
    field({ decoder: IDRegisterCart }),
    field({
        decoder: IDRegisterCart,
        version: 423,
        upgrade: function (cart) {
            for (const item of cart.deleteRegistrations) {
                item.cancellationFeePercentage = this._cancellationFeePercentage ?? 0;
            }
            return cart;
        },
    })
], IDRegisterCheckout.prototype, "cart", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], IDRegisterCheckout.prototype, "administrationFee", void 0);
__decorate([
    field({ decoder: IntegerDecoder }),
    field({ ...upgradePriceFrom2To4DecimalPlaces })
], IDRegisterCheckout.prototype, "freeContribution", void 0);
__decorate([
    field({ decoder: IntegerDecoder, version: 362, optional: true, field: 'cancellationFeePercentage' })
], IDRegisterCheckout.prototype, "_cancellationFeePercentage", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PaymentMethod), nullable: true })
], IDRegisterCheckout.prototype, "paymentMethod", void 0);
__decorate([
    field({ decoder: new URLDecoder({ allowedProtocols: ['https:'] }), nullable: true })
], IDRegisterCheckout.prototype, "redirectUrl", void 0);
__decorate([
    field({ decoder: new URLDecoder({ allowedProtocols: ['https:'] }), nullable: true })
], IDRegisterCheckout.prototype, "cancelUrl", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], IDRegisterCheckout.prototype, "asOrganizationId", void 0);
__decorate([
    field({ decoder: PaymentCustomer, nullable: true, version: 322 })
], IDRegisterCheckout.prototype, "customer", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true }),
    field({ ...upgradePriceFrom2To4DecimalPlaces, nullable: true })
], IDRegisterCheckout.prototype, "totalPrice", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 378 })
], IDRegisterCheckout.prototype, "sendConfirmationEmail", void 0);
export class RegisterCheckout {
    cart = new RegisterCart();
    administrationFee = 0;
    freeContribution = 0;
    paymentMethod = null;
    customer = null;
    asOrganizationId = null;
    /**
     * All outstanding items - not the one we are using to pay.
     * This is for validation and discount calculation.
     */
    balanceItems = null;
    /**
     * Only allowed to be changed when isAdminFromSameOrganization
     */
    sendConfirmationEmail = false; // Whether to send a confirmation email to the user after registration
    // Default hint for empty carts to know the organization to use
    defaultOrganization = null;
    /**
     * Note: only use this for temporary clones,
     * because the register items will still have a reference to the old checkout.
     */
    clone() {
        const checkout = new RegisterCheckout();
        checkout.cart = this.cart.clone();
        checkout.administrationFee = this.administrationFee;
        checkout.freeContribution = this.freeContribution;
        checkout.paymentMethod = this.paymentMethod;
        checkout.asOrganizationId = this.asOrganizationId;
        checkout.customer = this.customer ? this.customer.clone() : null;
        checkout.defaultOrganization = this.defaultOrganization;
        checkout.balanceItems = this.balanceItems?.slice() ?? null;
        return checkout;
    }
    convert() {
        return IDRegisterCheckout.create({
            cart: this.cart.convert(),
            administrationFee: this.administrationFee,
            freeContribution: this.freeContribution,
            paymentMethod: this.paymentMethod,
            totalPrice: this.totalPrice,
            asOrganizationId: this.asOrganizationId,
            customer: this.customer,
            sendConfirmationEmail: this.sendConfirmationEmail,
        });
    }
    get singleOrganization() {
        return this.cart.singleOrganization ?? this.defaultOrganization;
    }
    get singleOrganizationId() {
        return this.singleOrganization?.id ?? this.cart.deleteRegistrations[0]?.registration.registration.organizationId ?? this.cart.balanceItems[0]?.item.organizationId;
    }
    setDefaultOrganization(organization) {
        this.defaultOrganization = organization;
    }
    get isAdminFromSameOrganization() {
        return !!this.asOrganizationId && this.asOrganizationId === this.singleOrganization?.id;
    }
    add(item, options) {
        if (this.cart.isEmpty) {
            this.defaultOrganization = null;
        }
        if (!this.singleOrganization) {
            this.setDefaultOrganization(item.organization);
        }
        this.cart.add(item);
        if (options?.calculate !== false) {
            this.updatePrices();
        }
    }
    remove(item, options) {
        this.cart.remove(item);
        if (options?.calculate !== false) {
            this.updatePrices();
        }
        if (this.cart.isEmpty) {
            this.defaultOrganization = null;
        }
    }
    removeMemberAndGroup(memberId, groupId, options) {
        this.cart.removeMemberAndGroup(memberId, groupId);
        if (options?.calculate !== false) {
            this.updatePrices();
        }
        if (this.cart.isEmpty) {
            this.defaultOrganization = null;
        }
    }
    removeRegistration(registration, options) {
        this.cart.removeRegistration(registration);
        if (this.cart.isEmpty) {
            this.defaultOrganization = null;
        }
        if (options?.calculate !== false) {
            this.updatePrices();
        }
    }
    unremoveRegistration(registration, options) {
        this.cart.unremoveRegistration(registration);
        if (this.cart.isEmpty) {
            this.defaultOrganization = null;
        }
        if (options?.calculate !== false) {
            this.updatePrices();
        }
    }
    addBalanceItem(balanceItem, amount, options) {
        const item = BalanceItemCartItem.create({
            item: balanceItem,
            price: amount,
        });
        this.cart.addBalanceItem(item);
        if (options?.calculate !== false) {
            this.updatePrices();
        }
    }
    removeBalanceItem(item, options) {
        this.cart.removeBalanceItem(item);
        if (this.cart.isEmpty) {
            this.defaultOrganization = null;
        }
        if (options?.calculate !== false) {
            this.updatePrices();
        }
    }
    removeBalanceItemByBalance(item) {
        const _item = this.cart.balanceItems.find(i => i.item.id === item.id);
        if (!_item) {
            return;
        }
        this.removeBalanceItem(_item);
    }
    updatePrices() {
        this.cart.calculatePrices();
        this.applyMaximumDiscounts();
        if (this.isAdminFromSameOrganization || !this.singleOrganization) {
            this.administrationFee = 0;
        }
        else {
            this.administrationFee = this.singleOrganization.meta.registrationPaymentConfiguration.administrationFee.calculate(this.cart.price - this.bundleDiscount - this.cart.refund) ?? 0;
        }
    }
    /**
     * Optional behaviour required to automatically apply
     */
    updateBalances(memberBalanceItems) {
        this.balanceItems = memberBalanceItems;
        this.updatePrices();
    }
    /**
     * Find all negative balances for this user and members and apply them as
     * discount, the maximum possible value.
     */
    applyMaximumDiscounts() {
        if (this.isAdminFromSameOrganization) {
            return;
        }
        if (!this.balanceItems) {
            return;
        }
        // Remove all negative balance items
        for (const item of this.cart.balanceItems.slice()) {
            if (item.price < 0) {
                this.removeBalanceItem(item, { calculate: false });
            }
        }
        if (!this.singleOrganizationId) {
            return;
        }
        const discounts = RegisterCheckout.calculateAutomaticDiscounts({
            organizationId: this.singleOrganizationId,
            balanceItems: this.balanceItems,
            payingNow: this.cart.balanceItems.reduce((a, b) => a + b.price, 0),
            price: this.totalPrice - this.administrationFee - this.freeContribution,
        });
        for (const [item, amount] of discounts) {
            this.addBalanceItem(item, -amount, { calculate: false });
        }
    }
    /**
     * The part of the negative balance items (credits) that gets applied to a price.
     * Credits are only applied when the balance that remains open after this payment is negative.
     *
     * @param payingNow Open balance items that are being paid as part of the same price
     */
    static calculateAutomaticDiscounts({ organizationId, balanceItems, payingNow, price }) {
        const discounts = new Map();
        const items = balanceItems.filter(b => b.organizationId === organizationId);
        const totalOpenBalance = items.reduce((a, b) => a + b.priceOpen, 0) - payingNow;
        if (totalOpenBalance >= 0) {
            return discounts;
        }
        let remaining = Math.min(price, -totalOpenBalance);
        for (const item of items) {
            if (item.priceOpen >= 0) {
                continue;
            }
            const amount = Math.min(remaining, -item.priceOpen);
            if (amount <= 0) {
                break;
            }
            discounts.set(item, amount);
            remaining -= amount;
        }
        return discounts;
    }
    validate(data) {
        if (!this.isAdminFromSameOrganization && this.cart.deleteRegistrations.length > 0) {
            throw new SimpleError({
                code: 'forbidden',
                message: 'Permission denied: you are not allowed to delete registrations',
                human: $t(`%qr`),
            });
        }
        this.cart.validate(this, data ?? this.balanceItems);
        this.updatePrices();
        if (this.singleOrganization && (this.singleOrganization.meta.recordsConfiguration.freeContribution?.amounts.length ?? 0) === 0) {
            // Automatically clear free contribution if there are no options
            this.freeContribution = 0;
        }
        if (!this.isAdminFromSameOrganization) {
            if (this.totalPrice < 0) {
                // If the total price is negative, we cannot charge the user
                throw new SimpleError({
                    code: 'negative_price',
                    message: 'Total price cannot be negative',
                    human: $t(`%17E`),
                });
            }
        }
    }
    clear() {
        this.administrationFee = 0;
        this.freeContribution = 0;
        this.cart.items = [];
        this.cart.balanceItems = [];
        this.cart.deleteRegistrations = [];
        this.cart.bundleDiscounts = [];
        this.customer = null;
    }
    /**
     * Only includes 'due now' items - so excludes trials (doesn't work 100% yet with deleted registrations but this is not a problem at the moment)
     */
    get totalPrice() {
        return this.cart.price - this.bundleDiscount - this.cart.refund + this.administrationFee + this.freeContribution + this.cart.cancellationFees;
    }
    /**
     * Discounts that will be applied to items that are due now
     * (net, so minus the already applied discounts)
     */
    get bundleDiscount() {
        return this.cart.bundleDiscount;
    }
    /**
     * Discounts that will be applied to items that are due later
     * (net, so minus the already applied discounts)
     */
    get bundleDiscountDueLater() {
        return this.cart.bundleDiscountDueLater;
    }
    get showDiscountsSeparately() {
        return !this.isAdminFromSameOrganization;
    }
    getPriceBreakown(options) {
        const all = [];
        // Discounts
        for (const discount of this.cart.bundleDiscounts) {
            const value = discount.netTotal;
            if (value !== 0) {
                if (value < 0) {
                    all.push({
                        name: $t('%175') + ' (' + discount.name + ')',
                        price: -value,
                    });
                }
                else {
                    all.push({
                        name: discount.name,
                        price: -value,
                    });
                }
            }
        }
        let subtotal = this.cart.price + this.cart.priceDueLater;
        if (this.showDiscountsSeparately && this.cart.balanceItemDiscountsPrice !== 0) {
            subtotal -= this.cart.balanceItemDiscountsPrice;
            all.push({
                name: $t(`%1Xl`),
                price: this.cart.balanceItemDiscountsPrice,
                action: options?.balanceItemDiscountsAction,
            });
        }
        all.push(...[
            {
                name: $t(`%17F`),
                price: -this.cart.refund,
            },
            {
                name: $t(`%qs`),
                price: this.administrationFee,
            },
            {
                name: $t(`%Ot`),
                price: this.freeContribution,
            },
            {
                name: $t(`%17G`),
                price: this.cart.cancellationFees,
            },
        ].filter(a => a.price !== 0));
        if (all.length > 0 && subtotal !== 0) {
            all.unshift({
                name: $t(`%xJ`),
                price: subtotal,
            });
        }
        const totalLater = this.cart.priceDueLater - this.bundleDiscountDueLater;
        if (totalLater !== 0) {
            all.push({
                name: $t(`%10Z`),
                price: totalLater,
            });
        }
        if (this.isAdminFromSameOrganization) {
            all.push({
                name: (this.totalPrice >= 0 ? $t('%vb') : $t('%vc')),
                price: Math.abs(this.totalPrice),
            });
        }
        else {
            all.push({
                name: this.cart.priceDueLater ? $t(`%10c`) : $t(`%xL`),
                price: this.totalPrice,
            });
        }
        return all;
    }
    /**
     * @deprecated Use getPriceBreakown(options)
     */
    get priceBreakown() {
        return this.getPriceBreakown();
    }
}
//# sourceMappingURL=RegisterCheckout.js.map